import { useEffect, useRef, useState } from 'react';

import { type AssignedEntityLevel, type IAssignedAgent } from '../../../../interface/agent-assignment';
import { type IChildResultDetail, type IResultPartyVote } from '../../../../interface/child-agent';
import { getChildResultById } from '../../../../lib/child-result-api';
import { type CoverageState } from '../../overview/quick-analytics/utils';
import { getPartyColor } from '../../upload-result/party-colors';
import Lightbox from '../Lightbox';
import { fmtNum } from '../utils';
import TabMessage from './TabMessage';

type ViewState = 'loading' | 'ready' | 'empty' | 'error';

const DocIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M6 2.5h8L19 7v13a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V4A1.5 1.5 0 0 1 6 2.5Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
		<path
			d='M14 2.5V7h5M8.5 12h7M8.5 15.5h7'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
		/>
	</svg>
);
const WarnGlyph = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const partyLabel = (p: IResultPartyVote) => p.partyShortName || p.partyName || `Party ${p.partyId}`;

const SummaryGrid = ({ result }: { result: IChildResultDetail }) => {
	const rows: { label: string; value?: number }[] = [
		{ label: 'Registered voters', value: result.votersOnRegister },
		{ label: 'Accredited voters', value: result.accreditedVoters },
		{ label: 'Ballots issued', value: result.ballotPapersIssuedToPoolingUnit },
		{ label: 'Unused ballots', value: result.unusedBallotPapers },
		{ label: 'Rejected ballots', value: result.rejectedBallot },
		{ label: 'Total used ballots', value: result.totalUsedBallotPapers },
		{ label: 'Valid votes', value: result.totalValidVotes },
	];
	return (
		<div className='ca-result__grid'>
			{rows.map((r) => (
				<div
					className='ca-result__cell'
					key={r.label}
				>
					<span className='ca-result__cell-label'>{r.label}</span>
					<span className='ca-result__cell-value'>{fmtNum(r.value)}</span>
				</div>
			))}
		</div>
	);
};

const PartyBars = ({ result }: { result: IChildResultDetail }) => {
	const parties = [...(result.results ?? [])].sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0));
	if (!parties.length) return null;
	const tallied = parties.reduce((sum, p) => sum + (p.votes ?? 0), 0);
	const max = parties[0]?.votes || 1;
	const valid = result.totalValidVotes ?? 0;
	const mismatch = valid > 0 && tallied !== valid;
	const leader = parties[0];
	const leaderColor = getPartyColor(partyLabel(leader));

	return (
		<div className='ca-result__parties'>
			<div className='ca-result__parties-head'>
				<span className='ca-result__section-title'>Party votes</span>
				<span
					className='ca-result__leader'
					style={{ background: leaderColor.text, color: leaderColor.bg }}
				>
					Leading · {partyLabel(leader)} · {fmtNum(leader.votes)}
				</span>
			</div>
			<ul className='ca-result__bars'>
				{parties.map((p) => {
					const color = getPartyColor(partyLabel(p));
					const pct = Math.max(((p.votes ?? 0) / max) * 100, 2);
					return (
						<li
							className='ca-result__bar-row'
							key={`${p.partyId}-${p.partyName}`}
						>
							<span
								className='ca-result__bar-tag'
								style={{ background: color.bg, color: color.text }}
							>
								{partyLabel(p)}
							</span>
							<span className='ca-result__bar-track'>
								<span
									className='ca-result__bar-fill'
									style={{ width: `${pct}%`, background: color.bg }}
								/>
							</span>
							<span className='ca-result__bar-votes'>{fmtNum(p.votes)}</span>
						</li>
					);
				})}
			</ul>
			{mismatch ? (
				<p className='ca-result__mismatch'>
					<WarnGlyph />
					Party votes total {fmtNum(tallied)}, but the recorded valid votes are {fmtNum(valid)}.
				</p>
			) : null}
		</div>
	);
};

interface IResultTabProps {
	level: AssignedEntityLevel;
	resultId: string | null;
	coverage: CoverageState;
	agent: IAssignedAgent | null;
}

/**
 * Tab 2 — the child's uploaded result detail (sheet image, summary numbers,
 * per-party votes). Lazy-loads on first activation via the resultId the
 * assignment already carries. Handles not-uploaded / conflict / error cleanly.
 */
const ResultTab = ({ level, resultId, coverage, agent }: IResultTabProps) => {
	const [state, setState] = useState<ViewState>(resultId ? 'loading' : 'empty');
	const [result, setResult] = useState<IChildResultDetail | null>(null);
	const [lightbox, setLightbox] = useState<number | null>(null);
	const loadedRef = useRef(false);

	const load = () => {
		if (!resultId) {
			setState('empty');
			return;
		}
		setState('loading');
		getChildResultById(level, resultId).then((outcome) => {
			if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300 || !outcome.body) {
				setState('error');
				return;
			}
			setResult(outcome.body);
			setState('ready');
		});
	};

	useEffect(() => {
		if (loadedRef.current) return;
		loadedRef.current = true;
		load();
	}, []);

	if (state === 'loading') {
		return (
			<div className='ca-result__skeleton f-column-15'>
				<div className='ca-result__sk-image skeleton' />
				<div className='ca-result__sk-grid'>
					<div className='ca-result__sk-cell skeleton' />
					<div className='ca-result__sk-cell skeleton' />
					<div className='ca-result__sk-cell skeleton' />
					<div className='ca-result__sk-cell skeleton' />
				</div>
				<div className='ca-result__sk-bar skeleton' />
				<div className='ca-result__sk-bar skeleton' />
			</div>
		);
	}

	if (state === 'empty') {
		const phone = agent?.phoneNumber?.trim();
		return (
			<TabMessage
				icon={<DocIcon />}
				tone='neutral'
				title='No result submitted yet'
				hint='This agent has not uploaded a result for this entity. You can follow up directly.'
				action={phone ? { label: 'Call to follow up', href: `tel:${phone}` } : undefined}
			/>
		);
	}

	if (state === 'error' || !result) {
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='danger'
				title='We couldn’t load this result.'
				hint='The result detail is temporarily unavailable.'
				action={{ label: 'Try again', onClick: load }}
			/>
		);
	}

	const images = result.documentUrls?.filter(Boolean) ?? [];

	return (
		<div className='ca-result f-column-20'>
			{coverage === 'conflict' ? (
				<div className='ca-result__conflict'>
					<WarnGlyph />
					<span>More than one agent is linked to this entity — showing the most recent submission.</span>
				</div>
			) : null}

			{images.length ? (
				<div className='ca-result__sheets'>
					<span className='ca-result__section-title'>Result sheet{images.length > 1 ? 's' : ''}</span>
					<div className='ca-result__thumbs'>
						{images.map((url, i) => (
							<button
								type='button'
								className='ca-result__thumb'
								key={url}
								onClick={() => setLightbox(i)}
								aria-label={`Open result sheet ${i + 1}`}
							>
								<img
									src={url}
									alt={`Result sheet ${i + 1}`}
									loading='lazy'
								/>
								<span className='ca-result__thumb-zoom'>View</span>
							</button>
						))}
					</div>
				</div>
			) : null}

			<SummaryGrid result={result} />
			<PartyBars result={result} />

			{lightbox !== null ? (
				<Lightbox
					images={images}
					startIndex={lightbox}
					label='Result sheet'
					onClose={() => setLightbox(null)}
				/>
			) : null}
		</div>
	);
};

export default ResultTab;
